/*
 * A log of what the microphone did, readable on the phone itself.
 *
 * Dictation goes through the platform's own recogniser, and whether it works
 * depends on the device: the browser, its speech service, its permissions,
 * whether something else holds the microphone. None of that can be seen from
 * a laptop, and a phone has no console. So the front door keeps a short log
 * of every recogniser event and, when the page is opened with `?voice=debug`,
 * shows it on screen — which is the whole way to find out why a customer's
 * phone hears nothing.
 *
 * Off by default and invisible without the flag; the log itself costs a few
 * strings in memory and nothing else.
 */

const LIMIT = 60
const EVENT = 'blooq:voicelog'

const lines: string[] = []

/** True when the page was opened with the debug flag. */
export function voiceDebug(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return new URLSearchParams(window.location.search).get('voice') === 'debug'
  } catch {
    return false
  }
}

export function voiceLog(line: string) {
  const stamp = new Date().toISOString().slice(11, 23)
  lines.push(`${stamp} ${line}`)
  if (lines.length > LIMIT) lines.shift()
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVENT))
}

export function voiceLines(): readonly string[] {
  return lines
}

/** Re-run `listener` on every new line. Returns the unsubscribe. */
export function watchVoiceLog(listener: () => void): () => void {
  window.addEventListener(EVENT, listener)
  return () => window.removeEventListener(EVENT, listener)
}

/** What the device is, for the first line of the log. */
export function describeDevice(): string {
  if (typeof navigator === 'undefined') return 'no navigator'
  const scope = window as Window & { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown }
  const api = scope.SpeechRecognition
    ? 'SpeechRecognition'
    : scope.webkitSpeechRecognition
      ? 'webkitSpeechRecognition'
      : 'none'
  const coarse = window.matchMedia('(pointer: coarse)').matches
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  return [
    `api=${api}`,
    `lang=${navigator.language}`,
    `coarse=${coarse}`,
    `standalone=${standalone}`,
    `secure=${window.isSecureContext}`,
    `online=${navigator.onLine}`,
    `ua=${navigator.userAgent}`,
  ].join(' ')
}
